import { useState, useEffect, useCallback } from "react";
import { Network, RefreshCw, Power, Plus, Trash2, X, RotateCcw, Save, Loader2, CircleDot, AlertTriangle, Zap, Search, User, Download } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const VENDORS = [
  { v: "tplink", label: "TP-Link" }, { v: "mikrotik", label: "MikroTik" },
  { v: "dasan", label: "Dasan" }, { v: "cisco", label: "Cisco" },
  { v: "demo", label: "Demo (symulacja)" },
];
const emptyForm = () => ({ name: "", vendor: "tplink", host: "", port: "", username: "", password: "", secret: "", device_type: "", snmp_community: "" });

export default function SwitchMgmtPanel({ role, onClose }) {
  const { t } = useI18n();
  const isAdmin = role === "admin";
  const [devs, setDevs] = useState([]);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingSt, setLoadingSt] = useState(false);
  const [selPort, setSelPort] = useState(null);
  const [vlan, setVlan] = useState("");
  const [stats, setStats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [view, setView] = useState("ports");
  const [macRows, setMacRows] = useState([]);
  const [arpRows, setArpRows] = useState([]);
  const [loadingMac, setLoadingMac] = useState(false);
  const [loadingArp, setLoadingArp] = useState(false);
  const [q, setQ] = useState("");
  const [gq, setGq] = useState("");
  const [gRes, setGRes] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const downloadCsv = (filename, headers, rows) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const globalSearch = () => {
    if (gq.trim().length < 3) { toast.error(t("Podaj co najmniej 3 znaki")); return; }
    setGBusy(true);
    api.get("/switchmgmt/search", { params: { q: gq.trim() } })
      .then((r) => { setGRes(r.data?.results || []); if (!(r.data?.results || []).length) toast.info(t("Brak wyników na żadnym switchu")); })
      .catch((e) => toast.error(e.response?.data?.detail || t("Błąd wyszukiwania")))
      .finally(() => setGBusy(false));
  };

  const loadMac = useCallback((id) => {
    if (!id) return;
    setLoadingMac(true);
    api.get(`/switchmgmt/devices/${id}/mac-table`).then((r) => setMacRows(r.data?.entries || [])).catch(() => setMacRows([])).finally(() => setLoadingMac(false));
  }, []);
  const loadArp = useCallback((id) => {
    if (!id) return;
    setLoadingArp(true);
    api.get(`/switchmgmt/devices/${id}/arp-table`).then((r) => setArpRows(r.data?.entries || [])).catch(() => setArpRows([])).finally(() => setLoadingArp(false));
  }, []);
  const openClientByMac = async (mac) => {
    try {
      const { data } = await api.get("/switchmgmt/resolve-mac", { params: { mac } });
      if (data.found) {
        toast.success(`${t("Abonent")}: ${data.client_name || ""}`);
        window.dispatchEvent(new CustomEvent("fp-open-client360", { detail: { clientId: data.client_id } }));
      } else {
        toast.info(t("Brak powiązanego abonenta dla tego MAC"));
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd wyszukiwania abonenta")); }
  };

  const loadDevs = useCallback(() => {
    api.get("/switchmgmt/devices").then((r) => setDevs(r.data || [])).catch(() => setDevs([]));
  }, []);
  useEffect(() => { loadDevs(); const r = setTimeout(loadDevs, 900); return () => clearTimeout(r); }, [loadDevs]);

  const loadStatus = useCallback((id) => {
    if (!id) return;
    setLoadingSt(true); setSelPort(null);
    api.get(`/switchmgmt/devices/${id}/status`).then((r) => setStatus(r.data)).catch(() => setStatus({ online: false, ports: [], error: "Błąd" })).finally(() => setLoadingSt(false));
  }, []);
  useEffect(() => { if (sel) loadStatus(sel.id); else setStatus(null); setView("ports"); setMacRows([]); setArpRows([]); setQ(""); }, [sel, loadStatus]);
  useEffect(() => {
    if (!sel) return;
    if (view === "mac" && macRows.length === 0 && !loadingMac) loadMac(sel.id);
    if (view === "arp" && arpRows.length === 0 && !loadingArp) loadArp(sel.id);
  }, [view, sel]); // eslint-disable-line

  useEffect(() => {
    if (!selPort || !sel) { setStats([]); return; }
    let n = 0; setStats([]);
    const poll = () => api.get(`/switchmgmt/devices/${sel.id}/port-stats`, { params: { port: selPort.name } })
      .then((r) => setStats((s) => [...s.slice(-29), { t: ++n, in: r.data.in_mbps ?? 0, out: r.data.out_mbps ?? 0 }])).catch(() => {});
    poll(); const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [selPort, sel]);

  const saveDevice = async () => {
    if (!form.name || (form.vendor !== "demo" && !form.host)) { toast.error(t("Podaj nazwę i adres IP")); return; }
    try {
      await api.post("/switchmgmt/devices", { ...form, port: form.port ? Number(form.port) : undefined });
      toast.success(t("Dodano switch")); setShowForm(false); setForm(emptyForm()); loadDevs();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd zapisu")); }
  };
  const delDevice = async (id) => {
    if (!window.confirm(t("Usunąć switch z zarządzania?"))) return;
    try { await api.delete(`/switchmgmt/devices/${id}`); toast.success(t("Usunięto switch")); if (sel?.id === id) setSel(null); loadDevs(); } catch { toast.error(t("Błąd")); }
  };
  const portAction = async (port, body) => {
    try { await api.post(`/switchmgmt/devices/${sel.id}/port`, { port, ...body }); toast.success(t("Wykonano")); loadStatus(sel.id); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd sterowania")); }
  };
  const reboot = async () => {
    if (!window.confirm(t("Zrestartować cały switch?"))) return;
    try { await api.post(`/switchmgmt/devices/${sel.id}/reboot`); toast.success(t("Zlecono restart switcha")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd restartu")); }
  };
  const poeCycle = async (port) => {
    if (!window.confirm(t("Zrestartować zasilanie PoE na porcie? Urządzenie (kamera/AP/ONT) wyłączy się na chwilę."))) return;
    try { await api.post(`/switchmgmt/devices/${sel.id}/poe-cycle`, { port }); toast.success(t("Zlecono restart PoE portu")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd restartu PoE")); }
  };

  const ports = status?.ports || [];
  const rj = ports.filter((p) => !p.sfp);
  const sfp = ports.filter((p) => p.sfp);
  const portColor = (p) => p.admin === "down" ? "bg-red-900/60 border-red-600 text-red-200"
    : p.status === "up" ? "bg-green-600/80 border-green-400 text-white" : "bg-slate-700 border-slate-600 text-slate-400";

  const PortBox = ({ p }) => (
    <button onClick={() => { setSelPort(p); setVlan(p.vlan || ""); }} data-testid={`swport-${p.name}`}
      title={`${p.name} · ${p.status} · VLAN ${p.vlan || "-"}`}
      className={`w-10 h-9 rounded border text-[10px] font-mono flex items-center justify-center transition-all ${portColor(p)} ${selPort?.name === p.name ? "ring-2 ring-cyan-300 scale-105" : "hover:brightness-110"}`}>
      {p.index}
    </button>
  );

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col h-full" data-testid="switchmgmt-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 text-slate-200 font-semibold"><Network size={18} /> {t("Zarządzanie switchami")}</div>
        <button onClick={onClose} data-testid="switchmgmt-close" className="p-1 rounded hover:bg-slate-700 text-slate-400"><X size={18} /></button>
      </div>

      <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2 flex-wrap">
        <select value={sel?.id || ""} onChange={(e) => setSel(devs.find((d) => d.id === e.target.value) || null)} data-testid="switch-select"
          className="flex-1 min-w-[180px] bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
          <option value="">{devs.length ? t("Wybierz switch…") : t("Brak switchy — dodaj poniżej")}</option>
          {devs.map((d) => <option key={d.id} value={d.id}>{d.name} · {VENDORS.find((v) => v.v === d.vendor)?.label} · {d.host || "demo"}</option>)}
        </select>
        {sel && <button onClick={() => loadStatus(sel.id)} data-testid="switch-refresh" className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"><RefreshCw size={15} /></button>}
        {sel && isAdmin && <button onClick={reboot} data-testid="switch-reboot" className="px-2.5 py-1.5 rounded bg-amber-700 hover:bg-amber-600 text-xs flex items-center gap-1"><RotateCcw size={13} /> {t("Restart")}</button>}
        {sel && isAdmin && <button onClick={() => delDevice(sel.id)} data-testid="switch-delete" className="p-1.5 rounded bg-slate-800 hover:bg-red-700 text-slate-300"><Trash2 size={15} /></button>}
        {isAdmin && <button onClick={() => setShowForm((s) => !s)} data-testid="switch-add-toggle" className="px-2.5 py-1.5 rounded bg-cyan-700 hover:bg-cyan-600 text-xs flex items-center gap-1"><Plus size={13} /> {t("Dodaj")}</button>}
      </div>

      <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={gq} onChange={(e) => setGq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && globalSearch()} data-testid="switch-global-search"
            placeholder={t("Szukaj MAC lub IP na wszystkich switchach…")} className="w-full bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-sm text-slate-200" />
        </div>
        <button onClick={globalSearch} disabled={gBusy} data-testid="switch-global-search-btn" className="px-2.5 py-1.5 rounded bg-cyan-700 hover:bg-cyan-600 text-xs flex items-center gap-1 disabled:opacity-50">{gBusy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} {t("Szukaj wszędzie")}</button>
      </div>

      {showForm && isAdmin && (
        <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/40 grid grid-cols-2 gap-2" data-testid="switch-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Nazwa")} data-testid="switch-form-name" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          <select value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="switch-form-vendor" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
            {VENDORS.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
          </select>
          <input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder={t("Adres IP")} data-testid="switch-form-host" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder={form.vendor === "mikrotik" ? "Port API (8728)" : "Port SSH (22)"} data-testid="switch-form-port" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder={t("Login")} data-testid="switch-form-user" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("Hasło")} data-testid="switch-form-pass" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          {form.vendor !== "mikrotik" && form.vendor !== "demo" && (
            <input type="password" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} placeholder={t("Hasło enable (opcj.)")} data-testid="switch-form-secret" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          )}
          {form.vendor !== "mikrotik" && form.vendor !== "demo" && (
            <input value={form.snmp_community} onChange={(e) => setForm({ ...form, snmp_community: e.target.value })} placeholder={t("SNMP community (do statystyk, opcj.)")} data-testid="switch-form-snmp" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          )}
          <button onClick={saveDevice} data-testid="switch-form-save" className="bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-1.5 text-sm flex items-center justify-center gap-1.5"><Save size={14} /> {t("Zapisz")}</button>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4">
        {gRes !== null && (
          <div className="mb-4 rounded-lg border border-cyan-500/30 bg-slate-800/40 p-3" data-testid="switch-global-results">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-200 font-semibold">{t("Wyniki globalne")} ({gRes.length})</span>
              <div className="flex gap-2">
                <button onClick={() => downloadCsv("fiberpass-szukaj.csv", ["Switch", "Port", "MAC", "VLAN", "IP", "Typ"], gRes.map((r) => [r.switch, r.port, r.mac, r.vlan, r.ip, r.type]))} disabled={!gRes.length} data-testid="switch-global-csv" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1 disabled:opacity-40"><Download size={12} /> CSV</button>
                <button onClick={() => { setGRes(null); setGq(""); }} data-testid="switch-global-close" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1">{t("Zamknij")}</button>
              </div>
            </div>
            <div className="rounded border border-slate-800 max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-800/60 text-slate-400 sticky top-0"><tr>
                  <th className="text-left px-2 py-1.5">Switch</th><th className="text-left px-2 py-1.5">{t("Port")}</th>
                  <th className="text-left px-2 py-1.5">MAC</th><th className="text-left px-2 py-1.5">VLAN</th>
                  <th className="text-left px-2 py-1.5">IP</th>
                </tr></thead>
                <tbody>
                  {gRes.map((r, i) => (
                    <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/40">
                      <td className="px-2 py-1 text-slate-300">{r.switch}</td>
                      <td className="px-2 py-1 text-slate-200">{r.port}</td>
                      <td className="px-2 py-1"><button onClick={() => openClientByMac(r.mac)} data-testid={`gres-mac-${r.mac}`} className="font-mono text-cyan-200 hover:underline inline-flex items-center gap-1"><User size={10} className="opacity-60" />{r.mac || "—"}</button></td>
                      <td className="px-2 py-1 text-slate-400">{r.vlan || "—"}</td>
                      <td className="px-2 py-1 text-slate-400 font-mono">{r.ip || "—"}</td>
                    </tr>
                  ))}
                  {!gRes.length && <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-500">{t("Brak wyników")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!sel ? (
          <div className="text-slate-500 text-sm text-center mt-8">{t("Wybierz switch, aby zobaczyć panel portów.")}</div>
        ) : loadingSt ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm justify-center mt-8"><Loader2 className="animate-spin" size={16} /> {t("Łączenie z switchem…")}</div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 text-xs">
              <CircleDot size={13} className={status?.online ? "text-green-400" : "text-red-400"} />
              <span className={status?.online ? "text-green-300" : "text-red-300"}>{status?.online ? t("połączono") : t("brak połączenia")}</span>
              {status?.error && <span className="text-red-400 flex items-center gap-1" title={status.error}><AlertTriangle size={12} /> {t("Nie można połączyć — sprawdź IP, login i dostęp SSH/API")}</span>}
            </div>

            {status?.online && (
              <div className="flex gap-1 mb-3" data-testid="switch-view-tabs">
                {[["ports", t("Porty")], ["mac", t("Tablica MAC")], ["arp", "ARP"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setView(k)} data-testid={`switch-view-${k}`}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${view === k ? "bg-cyan-700 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{lbl}</button>
                ))}
              </div>
            )}

            {view === "ports" && status?.online && ports.length > 0 && (
              <div className="inline-block rounded-xl border border-cyan-500/20 bg-slate-900/70 backdrop-blur-md p-3 shadow-xl shadow-cyan-950/20" data-testid="switch-frontpanel">
                <div className="text-[10px] text-slate-500 uppercase mb-2">{t("Panel portów")} — {sel.name}</div>
                <div className="space-y-1">
                  <div className="flex gap-1">{rj.filter((_, i) => i % 2 === 0).map((p) => <PortBox key={p.name} p={p} />)}</div>
                  <div className="flex gap-1">{rj.filter((_, i) => i % 2 === 1).map((p) => <PortBox key={p.name} p={p} />)}</div>
                </div>
                {sfp.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-slate-700">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">SFP</div>
                    <div className="flex gap-1">{sfp.map((p) => <PortBox key={p.name} p={p} />)}</div>
                  </div>
                )}
                <div className="flex gap-3 mt-3 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-600 inline-block" /> {t("aktywny")}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-700 inline-block" /> {t("nieaktywny")}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-900 inline-block" /> {t("wyłączony")}</span>
                </div>
              </div>
            )}

            {view === "ports" && selPort && (
              <div className="mt-4 rounded-xl border border-cyan-500/20 bg-slate-900/70 backdrop-blur-md p-3 max-w-md shadow-xl shadow-cyan-950/20 kv-anim-in" data-testid="switch-port-panel">
                <div className="text-sm font-semibold text-cyan-300 mb-2">{t("Port")} {selPort.name}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mb-3">
                  <div className="text-slate-500">{t("Status")}</div><div className="text-slate-200">{selPort.status}</div>
                  <div className="text-slate-500">{t("Administracyjnie")}</div><div className="text-slate-200">{selPort.admin}</div>
                  <div className="text-slate-500">{t("Prędkość")}</div><div className="text-slate-200">{selPort.speed ? `${selPort.speed} Mb/s` : "—"}</div>
                  <div className="text-slate-500">VLAN</div><div className="text-slate-200">{selPort.vlan || "—"}</div>
                </div>

                <div className="mb-3" data-testid="swport-traffic">
                  <div className="flex items-center justify-between text-[10px] text-slate-500 uppercase mb-1">
                    <span>{t("Ruch na żywo")}</span>
                    <span className="flex gap-3 normal-case">
                      <span className="text-cyan-300">↓ {stats.length ? stats[stats.length - 1].in : 0} Mb/s</span>
                      <span className="text-amber-300">↑ {stats.length ? stats[stats.length - 1].out : 0} Mb/s</span>
                    </span>
                  </div>
                  <div className="h-24 bg-slate-950/50 rounded border border-slate-800">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                        <YAxis width={30} tick={{ fontSize: 9, fill: "#64748b" }} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }} labelFormatter={() => ""} formatter={(v, n) => [`${v} Mb/s`, n === "in" ? t("pobieranie") : t("wysyłanie")]} />
                        <Line type="monotone" dataKey="in" stroke="#22d3ee" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="out" stroke="#fbbf24" strokeWidth={2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="mb-3" data-testid="swport-devices">
                  <div className="flex items-center justify-between text-[10px] text-slate-500 uppercase mb-1">
                    <span>{t("Urządzenia na porcie")}</span>
                    <button onClick={() => loadMac(sel.id)} data-testid="swport-mac-load" className="text-cyan-400 hover:text-cyan-300 normal-case flex items-center gap-1"><RefreshCw size={11} className={loadingMac ? "animate-spin" : ""} /> {t("odśwież")}</button>
                  </div>
                  {(() => {
                    if (loadingMac) return <div className="text-[11px] text-slate-500">{t("Pobieranie…")}</div>;
                    const list = macRows.filter((m) => m.port === selPort.name);
                    if (!list.length) return <div className="text-[11px] text-slate-500">{macRows.length ? t("Brak urządzeń na tym porcie") : t("Kliknij „odśwież”, aby pobrać MAC-i")}</div>;
                    const ipByMac = {}; arpRows.forEach((a) => { if (a.mac) ipByMac[a.mac] = a.ip; });
                    return <div className="space-y-0.5 max-h-24 overflow-auto">{list.map((m, i) => (
                      <div key={i} className="flex justify-between text-[11px] font-mono"><button onClick={() => openClientByMac(m.mac)} data-testid={`swport-mac-${m.mac}`} title={t("Przejdź do abonenta")} className="text-cyan-200 hover:text-cyan-100 hover:underline inline-flex items-center gap-1"><User size={10} className="opacity-60" />{m.mac}</button><span className="text-slate-400">{ipByMac[m.mac] || (m.vlan ? "VLAN " + m.vlan : "")}</span></div>
                    ))}</div>;
                  })()}
                </div>
                {isAdmin && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {selPort.admin === "up" ? (
                        <button onClick={() => portAction(selPort.name, { admin: "down" })} data-testid="swport-disable" className="flex-1 bg-red-800 hover:bg-red-700 rounded py-1.5 text-xs flex items-center justify-center gap-1"><Power size={13} /> {t("Wyłącz port")}</button>
                      ) : (
                        <button onClick={() => portAction(selPort.name, { admin: "up" })} data-testid="swport-enable" className="flex-1 bg-green-700 hover:bg-green-600 rounded py-1.5 text-xs flex items-center justify-center gap-1"><Power size={13} /> {t("Włącz port")}</button>
                      )}
                      <button onClick={() => poeCycle(selPort.name)} data-testid="swport-poe-cycle" title={t("Restart zasilania PoE (reboot kamery/AP/ONT)")} className="flex-1 bg-indigo-700 hover:bg-indigo-600 rounded py-1.5 text-xs flex items-center justify-center gap-1"><Zap size={13} /> {t("Restart PoE")}</button>
                    </div>
                    {(
                      <div className="flex gap-2">
                        <input value={vlan} onChange={(e) => setVlan(e.target.value)} placeholder="VLAN ID" data-testid="swport-vlan-input" className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                        <button onClick={() => vlan && portAction(selPort.name, { vlan: Number(vlan) })} data-testid="swport-vlan-set" className="bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-1.5 text-xs">{t("Ustaw VLAN")}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {view === "mac" && (
              <div data-testid="switch-mac-table">
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Szukaj MAC / port / VLAN…")} data-testid="switch-mac-search"
                      className="w-full bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-xs text-slate-200" />
                  </div>
                  <button onClick={() => loadMac(sel.id)} data-testid="switch-mac-refresh" className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"><RefreshCw size={14} className={loadingMac ? "animate-spin" : ""} /></button>
                  <button onClick={() => downloadCsv(`mac-${sel.name}.csv`, ["MAC", "Port", "VLAN", "Typ"], macRows.map((m) => [m.mac, m.port, m.vlan, m.type]))} disabled={!macRows.length} data-testid="switch-mac-csv" title="CSV" className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"><Download size={14} /></button>
                </div>
                {loadingMac ? (
                  <div className="text-slate-400 text-sm flex items-center gap-2 justify-center mt-6"><Loader2 className="animate-spin" size={15} /> {t("Pobieranie tablicy MAC…")}</div>
                ) : (
                  <div className="rounded-lg border border-slate-800 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-800/60 text-slate-400"><tr>
                        <th className="text-left px-2 py-1.5">MAC</th><th className="text-left px-2 py-1.5">{t("Port")}</th>
                        <th className="text-left px-2 py-1.5">VLAN</th><th className="text-left px-2 py-1.5">{t("Typ")}</th>
                      </tr></thead>
                      <tbody>
                        {macRows.filter((m) => !q || `${m.mac} ${m.port} ${m.vlan}`.toLowerCase().includes(q.toLowerCase())).map((m, i) => (
                          <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-default">
                            <td className="px-2 py-1"><button onClick={() => openClientByMac(m.mac)} data-testid={`macrow-${m.mac}`} title={t("Przejdź do abonenta")} className="font-mono text-cyan-200 hover:text-cyan-100 hover:underline inline-flex items-center gap-1"><User size={11} className="opacity-60" />{m.mac}</button></td>
                            <td className="px-2 py-1 text-slate-200">{m.port}</td>
                            <td className="px-2 py-1 text-slate-400">{m.vlan || "—"}</td>
                            <td className="px-2 py-1 text-slate-400">{m.type || "—"}</td>
                          </tr>
                        ))}
                        {macRows.length === 0 && <tr><td colSpan={4} className="px-2 py-4 text-center text-slate-500">{t("Brak wpisów MAC")}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-1.5">{t("Łącznie")}: {macRows.length}</div>
              </div>
            )}

            {view === "arp" && (
              <div data-testid="switch-arp-table">
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Szukaj IP / MAC / interfejs…")} data-testid="switch-arp-search"
                      className="w-full bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-xs text-slate-200" />
                  </div>
                  <button onClick={() => loadArp(sel.id)} data-testid="switch-arp-refresh" className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"><RefreshCw size={14} className={loadingArp ? "animate-spin" : ""} /></button>
                  <button onClick={() => downloadCsv(`arp-${sel.name}.csv`, ["IP", "MAC", "Interfejs"], arpRows.map((a) => [a.ip, a.mac, a.interface]))} disabled={!arpRows.length} data-testid="switch-arp-csv" title="CSV" className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"><Download size={14} /></button>
                </div>
                {loadingArp ? (
                  <div className="text-slate-400 text-sm flex items-center gap-2 justify-center mt-6"><Loader2 className="animate-spin" size={15} /> {t("Pobieranie tablicy ARP…")}</div>
                ) : (
                  <div className="rounded-lg border border-slate-800 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-800/60 text-slate-400"><tr>
                        <th className="text-left px-2 py-1.5">IP</th><th className="text-left px-2 py-1.5">MAC</th>
                        <th className="text-left px-2 py-1.5">{t("Interfejs")}</th>
                      </tr></thead>
                      <tbody>
                        {arpRows.filter((a) => !q || `${a.ip} ${a.mac} ${a.interface}`.toLowerCase().includes(q.toLowerCase())).map((a, i) => (
                          <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/40">
                            <td className="px-2 py-1 text-slate-200">{a.ip}</td>
                            <td className="px-2 py-1 font-mono text-cyan-200">{a.mac || "—"}</td>
                            <td className="px-2 py-1 text-slate-400">{a.interface || "—"}</td>
                          </tr>
                        ))}
                        {arpRows.length === 0 && <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-500">{t("Brak wpisów ARP")}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-1.5">{t("Łącznie")}: {arpRows.length}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
